import React, { useMemo, useState } from "react";
import { Copy, Check, QrCode, ExternalLink, Smartphone, Bot, GraduationCap, Globe } from "lucide-react";
import { useT } from "../i18n";
import { INT } from "../constants/testIds";

export default function SocialIntegrations({ lang }) {
    const T = useT(lang);

    const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";

    // Dynamic deep links into the app for marketing/sharing
    const links = useMemo(() => ([
        {
            id: "landing",
            title: T.botLandingLink,
            url: `${currentOrigin}/`,
            icon: Globe,
            accent: "#1A365D",
        },
        {
            id: "bot",
            title: T.botSimLink,
            url: `${currentOrigin}/?tab=simulator`,
            icon: Bot,
            accent: "#D4AF37",
        },
        {
            id: "peda",
            title: T.pedaLink,
            url: `${currentOrigin}/?tab=peda`,
            icon: GraduationCap,
            accent: "#22C55E",
        },
    ]), [currentOrigin, T]);

    const [copiedId, setCopiedId] = useState(null);
    const copy = async (id, url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1800);
        } catch { /* noop */ }
    };

    const qrUrl = (data) =>
        `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data)}&color=1A365D&bgcolor=FFFFFF&margin=10`;

    return (
        <div className="fade-up">
            <div className="mb-8">
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] gold-underline inline-block">
                    {T.intTitle}
                </h2>
                <p className="text-[#475569] mt-3 max-w-2xl">{T.intSubtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {links.map((l) => {
                    const Icon = l.icon;
                    return (
                        <div key={l.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 card-elevate">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: l.accent }}>
                                    <Icon size={22} color="#fff" />
                                </div>
                                <h3 className="font-serif text-lg font-bold text-[#1A365D] flex-1">{l.title}</h3>
                            </div>

                            <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3 mb-4">
                                <div className="text-[11px] font-mono break-all text-[#0F172A] select-all" dir="ltr">
                                    {l.url}
                                </div>
                            </div>

                            <div className="flex flex-col items-center gap-3 mb-4">
                                <div className="bg-white border border-[#D4AF37] rounded-xl p-3 shadow-sm">
                                    <img
                                        src={qrUrl(l.url)}
                                        alt={`QR ${l.id}`}
                                        className="w-40 h-40 object-contain"
                                        referrerPolicy="no-referrer"
                                        data-testid={INT.qrImg(l.id)}
                                    />
                                </div>
                                <div className="text-xs text-[#475569] flex items-center gap-1.5">
                                    <Smartphone size={12} />
                                    {T.scanQr}
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => copy(l.id, l.url)}
                                    className="btn-outline flex-1 justify-center !py-2 !text-sm"
                                    data-testid={INT.copyBtn(l.id)}
                                >
                                    {copiedId === l.id ? <Check size={16} /> : <Copy size={16} />}
                                    {copiedId === l.id ? T.copied : T.copyLink}
                                </button>
                                <a
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-royal !py-2 !text-sm"
                                >
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Footer notice */}
            <div className="mt-8 bg-[#F8F9FA] border border-[#E2E8F0] rounded-xl p-5 flex items-start gap-3">
                <QrCode size={22} color="#D4AF37" className="mt-0.5 shrink-0" />
                <div>
                    <h4 className="font-bold text-[#1A365D] mb-1">
                        {lang === "ar" ? "روابط ديناميكية تلقائيًا" : "קישורים דינמיים אוטומטית"}
                    </h4>
                    <p className="text-sm text-[#475569] leading-relaxed">
                        {lang === "ar"
                            ? "تُولَّد الروابط ورموز QR تلقائيًا بناءً على عنوان النشر الحالي، فلا يحدث 'كسر' للروابط بين بيئات التطوير والإنتاج."
                            : "הקישורים וקודי ה-QR נוצרים אוטומטית בהתאם לכתובת ההפעלה הנוכחית — ללא 'שבירת קישורים' בין סביבות פיתוח לייצור."}
                    </p>
                </div>
            </div>
        </div>
    );
}
